
import * as React from 'react';
import { ISelection } from 'office-ui-fabric-react/lib/Selection';
import { Tile, ITileProps, TileSize, getTileLayout, renderTileWithLayout } from '@uifabric/experiments/lib/Tile';
import {
    FolderCover as FolderCoverBase,
    IFolderCoverProps,
    renderFolderCoverWithLayout,
    getFolderCoverLayout
} from '@uifabric/experiments/lib/FolderCover';
import { SignalField, SharedSignal } from '@uifabric/experiments/lib/components/signals/Signals';
import { BaseText } from '../fields/baseText/BaseTextField';
import * as ItemTileStyles from './ItemTile.scss';
import { ItemFileTypeIcon } from '../itemFileTypeIcon/ItemFileTypeIcon';
import { getLifeCycleSignals, ISignalData } from '../fields/signal/SignalIcon';
import { itemAriaLabelControl } from '../../controls/itemAriaLabelControl';
import { getRowAriaLabel as defaultGetRowAriaLabel } from '../../models/items/AriaHelper';
import { ItemThumbnail } from '../itemThumbnail/ItemThumbnail';
import { WithItemCache, IItemCacheStateProps } from '../withItemCache/WithItemCache';
import { css } from '@uifabric/utilities/lib/css';
import { displayNameFacet, dateModifiedFacet, folderFacet, isSharedFacet } from '../../facets/ItemFacets';
import { isGroupFacet, countFacet } from '../../facets/GroupFacets';
import { thumbnailFacet } from '../../facets/MediaFacets';
import { IDecoratedProps } from '../../utilities/components/Decorators';
import { getItemHoverCard } from '../fileHoverCard/getItemHoverCard';
import { Fragment } from '../../utilities/components/Fragment';
import { itemTileControl } from '../../controls/itemTileControl';
import { IComponentAsProps } from '@uifabric/utilities/lib/IComponentAs';
import { ISpecifiedItemProps } from '../decorators/selectors';
import { configurationItemKey, enableFluentIconsFacet } from '../../facets/ConfigurationFacets';
import { DateRenderer } from '../fields/date/DateRenderer';
import { oneNoteFacet } from '../../facets/DocumentFacets';

// TODO: huaxi, remove it once we have fabric bump
const FolderCover: React.ComponentType<IFolderCoverProps & {
    isFluent?: boolean;
}> = FolderCoverBase;
/**
 * @internal
 */
export interface ISelectionContext {
    selection: ISelection;
}

/**
 * @public
 */
export type IItemTileMode = 'document' | 'media';

/**
 * @public
 */
export type IItemTileProps = IDecoratedProps<
    ITileProps,
    Pick<ITileProps,
    'itemName' |
    'ariaLabel' |
    'itemActivity' |
    'foreground' |
    'background' |
    'showBackgroundFrame' |
    'showForegroundFrame'>, {
        itemKey: string;
        itemTileMode?: IItemTileMode;
    }>;

/**
 * @public
 */
export interface IItemTileState {
    thumbnailFailed?: boolean;
    thumbnailLoaded?: boolean;
}

const SIZES: {
    [P in TileSize]: {
        iconSize: 48 | 64;
    }
} = {
    small: {
        iconSize: 48
    },
    large: {
        iconSize: 64
    }
};

const DefaultTile = (props: IComponentAsProps<ITileProps & ISpecifiedItemProps>): JSX.Element => {
    const {
        itemKey,
        ...tileProps
    } = props;

    return (
        <Tile { ...tileProps } />
    );
};

/**
 * @public
 */
export class ItemTile extends React.PureComponent<IItemTileProps, IItemTileState> {
    constructor(props: IItemTileProps) {
        super(props);
        this.state = {};
    }

    public render(): JSX.Element {
        const {
            itemKey,
            className,
            itemTileMode = 'document',
            ...tileProps
        } = this.props;

        const {
            tileSize = 'large',
        } = this.props;

        const {
            thumbnailLoaded,
            thumbnailFailed
        } = this.state;

        return (
            <WithItemCache>
                { ({ itemCache }: IItemCacheStateProps) => {
                    const {
                        displayName,
                        folder,
                        oneNote,
                        isGroup,
                        count,
                        isShared
                    } = itemCache.demandItem({
                        displayName: displayNameFacet,
                        dateModified: dateModifiedFacet,
                        folder: folderFacet,
                        oneNote: oneNoteFacet,
                        isGroup: isGroupFacet,
                        count: countFacet,
                        isShared: isSharedFacet
                    }, itemKey);

                    const itemTileControlHandler = itemCache.demandItemFacet(itemTileControl, itemKey);

                    const {
                        tileAs: TileAs,
                        itemName,
                        itemActivity
                    } = itemTileControl.evaluate(itemTileControlHandler)(itemCache, {
                        itemKey,
                        itemName: (
                            <BaseText
                                className={ ItemTileStyles.name }
                                data-automationid='name'
                                text={ displayName || '' }
                                title={ displayName || '' }
                            />
                        ),
                        itemActivity: (
                            <DateRenderer itemKey={ itemKey } />
                        ),
                        tileAs: DefaultTile
                    });

                    const lifeCycleSignalData = getLifeCycleSignals(itemCache, itemKey, 1, true);
                    const lifeCycleSignal = lifeCycleSignalData && lifeCycleSignalData.map((signalData: ISignalData) => {
                        return signalData.signal;
                    });

                    const isThumbnailResolved = itemCache.isItemFacetResolved(thumbnailFacet, itemKey);
                    const thumbnail = isThumbnailResolved ? itemCache.demandItemFacet(thumbnailFacet, itemKey) : undefined;

                    // Get aria label for the tile. Can use default aria label or override in addons.
                    const itemAriaLabelControlHandler = itemCache.demandItemFacet(itemAriaLabelControl, itemKey);
                    const defaultLabel = defaultGetRowAriaLabel(itemCache, { itemKey: itemKey });
                    const { itemAriaLabel } = itemAriaLabelControl.evaluate(itemAriaLabelControlHandler)(itemCache, {
                        itemKey: itemKey,
                        itemAriaLabel: defaultLabel
                    });
                    const isFluentIconEnabled = itemCache.demandItemFacet(enableFluentIconsFacet, configurationItemKey);

                    const tile = (
                        <TileAs
                            itemKey={ itemKey }
                            className={ css(ItemTileStyles.tile, className, {
                                [ItemTileStyles.hideThumbnail]: !thumbnailLoaded || !!thumbnailFailed,
                                [ItemTileStyles.hideFileTypeIcon]: !!thumbnailLoaded || !!thumbnail && !thumbnailFailed
                            }) }
                            itemName={ (
                                <SignalField
                                    signalsFieldMode='compact'
                                    before={ lifeCycleSignal }
                                >
                                    { itemName }
                                </SignalField>
                            ) }
                            ariaLabel={ itemAriaLabel }
                            data-automationid={ 'ItemTile' }
                            itemActivity={ (
                                <SignalField>
                                    { itemActivity }
                                </SignalField>
                            ) }
                            { ...tileProps }
                            showForegroundFrame={ itemTileMode === 'document' && (!folder || !!oneNote) }
                            showBackgroundFrame={ itemTileMode === 'media' }
                            hideForeground={
                                (!!thumbnail && !thumbnailLoaded)
                            }
                            hideBackground={
                                !thumbnail || !thumbnailLoaded
                            }
                            foreground={ <div /> }
                        />
                    );

                    const {
                        foregroundSize = { width: 0, height: 0 },
                        backgroundSize = { width: 0, height: 0 }
                    } = getTileLayout(tile);

                    const foreground: JSX.Element[] = [];
                    const background: JSX.Element[] = [];

                    if (folder && !oneNote || isGroup) {
                        const folderCover = (
                            <FolderCover
                                key='folder'
                                className={ ItemTileStyles.folderCover }
                                folderCoverSize={ this.props.tileSize }
                                folderCoverType={ 'default' }
                                metadata={ count }
                                signal={ this._getFolderCoverSignal(isShared) }
                                isFluent={ isFluentIconEnabled }
                            />
                        );

                        const {
                            contentSize
                        } = getFolderCoverLayout(folderCover);

                        foreground.push(
                            renderFolderCoverWithLayout(folderCover, {
                                children: count ? (
                                    <span
                                        className={ ItemTileStyles.blankFolderCover }
                                        style={
                                            {
                                                width: `${contentSize.width}px`,
                                                height: `${contentSize.height}px`
                                            }
                                        }
                                    />
                                ) : null
                            })
                        );
                    } else {
                        if (!thumbnailFailed && (!isThumbnailResolved || thumbnailLoaded || thumbnail)) {
                            if (itemTileMode === 'media') {
                                background.push(
                                    <ItemThumbnail
                                        key='thumbnail'
                                        itemKey={ itemKey }
                                        className={ ItemTileStyles.thumbnail }
                                        desiredSize={ backgroundSize }
                                        onError={ this._onThumbnailError }
                                        onLoad={ this._onThumbnailLoad }
                                    />
                                );
                            } else {
                                foreground.push(
                                    <ItemThumbnail
                                        key='thumbnail'
                                        itemKey={ itemKey }
                                        className={ ItemTileStyles.thumbnail }
                                        desiredSize={ foregroundSize }
                                        onError={ this._onThumbnailError }
                                        onLoad={ this._onThumbnailLoad }
                                    />
                                );
                            }
                            foreground.push(
                                <ItemFileTypeIcon
                                    key='fileTypeIcon'
                                    itemKey={ itemKey }
                                    className={ !!thumbnailLoaded ? ItemTileStyles.tileAppIcon : ItemTileStyles.fileTypeIcon }
                                    size={ !!thumbnailLoaded ? 16 : SIZES[tileSize].iconSize }
                                    preferAppIcon={ false }
                                />
                            );
                        } else {
                            if (!isThumbnailResolved || !thumbnailLoaded) {
                                foreground.push(
                                    <ItemFileTypeIcon
                                        key='fileTypeIcon'
                                        itemKey={ itemKey }
                                        className={ ItemTileStyles.fileTypeIcon }
                                        preferAppIcon={ false }
                                        size={ SIZES[tileSize].iconSize }
                                    />
                                );
                            }
                        }
                    }

                    const tileWithLayout = renderTileWithLayout(tile, {
                        foreground: foreground.length > 0 ? foreground : null,
                        background: background.length > 0 ? background : null
                    });

                    const HoverCard = getItemHoverCard(itemCache, { itemKey });

                    return (
                        <Fragment>
                            { HoverCard ?
                                <HoverCard itemKey={ itemKey }>
                                    { tileWithLayout }
                                </HoverCard> :
                                tileWithLayout
                            }
                        </Fragment>
                    );
                } }
            </WithItemCache>
        );
    }

    public componentWillReceiveProps(nextProps: IItemTileProps): void {
        if (nextProps.itemKey !== this.props.itemKey) {
            this.setState({
                thumbnailLoaded: false,
                thumbnailFailed: false
            });
        }
    }

    private _onThumbnailLoad = (): void => {
        this.setState({
            thumbnailLoaded: true
        });
    }

    private _onThumbnailError = (): void => {
        this.setState({
            thumbnailFailed: true
        });
    }

    private _getFolderCoverSignal(isShared: boolean | undefined): React.ReactNode {
        return (
            isShared ? <SharedSignal /> : null
        );
    }
}
